import type { TFunction } from 'i18next';
import type { QuickSearchResponse } from '../../../data/quickSearch/useQuickSearch';
import { IconAi } from '../../../styled-components/components/Icon';
import type { NavigationPage } from '../NavigationV2/NavigationProvider/utils';
import type { QuickMenuOptions, QuickSearchResultListItem, QuickSearchResultListSection } from '.';

export const MIN_SEARCH_TERM_LENGTH = 2;

export const findPagesWithTerm = (
  pages: NavigationPage[],
  searchTerm: string,
): NavigationPage[][] => {
  if (!searchTerm) {
    return [];
  }
  const searchLowerCase = searchTerm.toLowerCase();
  const results: NavigationPage[][] = [];

  const search = (pages: NavigationPage[], path: NavigationPage[]): void => {
    pages.forEach((page) => {
      const matches = page.title.toLowerCase().includes(searchLowerCase);
      const newPath = [...path, page]; // Extend the current path with the current page
      if (matches) {
        // If the current page matches the search term, add the path to the results
        results.push(newPath);
      }
      // Recursively search child pages, if any
      if (page.pages) {
        search(page.pages, newPath);
      }
    });
  };

  search(pages, []);
  return results;
};

export const prepareSections = (
  searchResults: QuickSearchResponse | undefined,
  navigationHits: NavigationPage[][],
  query: string,
  branchId: string | undefined,
  t: TFunction,
  options: QuickMenuOptions,
): [boolean, QuickSearchResultListSection[]] => {
  const sections: QuickSearchResultListSection[] = [];

  const vehicleListItems: QuickSearchResultListItem[] = [];

  if (!options.useAi && query.trim().split(' ').length > MIN_SEARCH_TERM_LENGTH) {
    vehicleListItems.push({
      header: t('ai.header', { ns: 'quickSearch' }),
      title: t('ai.title', { ns: 'quickSearch' }),
      doNotHighlight: true,
      type: 'ai-toggle',
      tone: 'secondary',
      icon: <IconAi />,
    });
  }

  // Vehicle Hits Section
  if (searchResults?.vehicle_hits?.length) {
    const results = searchResults.vehicle_hits.map(
      (vehicle): QuickSearchResultListItem => ({
        header: `${vehicle.vehicle.manufacturer} ${vehicle.vehicle.salesName}`,
        title: vehicle.vehicle.registrationNumber,
        url: `/${branchId}/vehicles/administrate-vehicles/${vehicle.id}`,
        vehicle,
        type: 'vehicle',
      }),
    );

    vehicleListItems.push(...results);

    vehicleListItems.push({
      title: t('goToSearch.title', { ns: 'quickSearch' }),
      url: `/${branchId}/vehicles/administrate-vehicles?${searchResults.vehicle_query}`,
      doNotHighlight: true,
      type: 'vehicle-listing',
    });
  }

  if (vehicleListItems.length) {
    sections.push({
      title: t('sections.vehicles', { ns: 'quickSearch' }),
      subtitle: options.useAi ? t('ai.results', { ns: 'quickSearch' }) : undefined,
      query: searchResults?.vehicle_query,
      items: vehicleListItems,
    });
  }

  // Navigation Hits Section
  if (navigationHits.length && query.length >= MIN_SEARCH_TERM_LENGTH) {
    sections.push({
      title: t('sections.pages', { ns: 'quickSearch' }),
      items: navigationHits.slice(0, 6).map((path) => ({
        header: path.map((p) => p.title).join(' / '),
        title: path[path.length - 1].title,
        url: path[path.length - 1].url,
        type: 'page',
      })),
    });
  }

  const faqItems = searchResults?.hubspot_hits?.filter(({ type }) => type === 'KNOWLEDGE_ARTICLE');
  const blogItems = searchResults?.hubspot_hits?.filter(({ type }) => type === 'BLOG_POST');

  // Hubspot Hits Section
  if (faqItems?.length) {
    sections.push({
      title: t('sections.faq', { ns: 'quickSearch' }),
      items: faqItems.map((hit) => ({
        header: hit.category,
        title: hit.title,
        url: hit.url,
        isExternal: true,
        type: 'faq',
      })),
    });
  }

  if (blogItems?.length) {
    sections.push({
      title: t('sections.blog', { ns: 'quickSearch' }),
      items: blogItems.map((hit) => ({
        header: hit.category,
        title: hit.title,
        url: hit.url,
        isExternal: true,
        type: 'faq',
      })),
    });
  }

  const hasHits =
    !!navigationHits.length ||
    !!searchResults?.vehicle_hits?.length ||
    !!searchResults?.hubspot_hits?.length;

  return [hasHits, sections];
};
